"use strict";
/**
 * Supplier cost engine (GH547).
 *
 * Pure functions, shared by the backend and the screens so both compute the same number:
 *
 * - `selectApplicablePromotions` — which promotions apply to one supplier code on a date, with the
 *   precedence rule: a promotion on specific codes (`items`) beats one on the whole line (`lab_line`).
 * - `computeEffectiveCost` — what one of OUR purchase units really costs from one supplier, at the
 *   quantity being ordered, after VAT, pack size, the best promotion and early payment.
 * - `recommendHabitual` — which supplier should be the habitual one (decision D3).
 *
 * Every trap in `.claude/rules/compras-promociones.md` §4 is a test of this file.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HABITUAL_CHANGE_MIN_PERCENT = exports.HABITUAL_CHANGE_MIN_ANNUAL_SAVING = void 0;
exports.isPromotionValidOn = isPromotionValidOn;
exports.selectApplicablePromotions = selectApplicablePromotions;
exports.computeEffectiveCost = computeEffectiveCost;
exports.recommendHabitual = recommendHabitual;
const supplier_linking_enums_1 = require("../constants/supplier-linking.enums");
const norm = (s) => String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toUpperCase();
/** `true` when `date` (YYYY-MM-DD) falls inside the promotion's validity, both ends inclusive. */
function isPromotionValidOn(promotion, date) {
    const { from, to } = promotion.validity;
    if (from && date < from.slice(0, 10))
        return false;
    if (to && date > to.slice(0, 10))
        return false;
    return true;
}
function scopeMatches(promotion, target) {
    const { scope } = promotion;
    if (scope.kind === supplier_linking_enums_1.PromotionScopeKind.items)
        return (scope.skus ?? []).includes(target.sku);
    if (scope.kind === supplier_linking_enums_1.PromotionScopeKind.lab_line) {
        if (!promotion.laboratory || norm(promotion.laboratory) !== norm(target.lab))
            return false;
        return !(scope.excludeSkus ?? []).includes(target.sku);
    }
    return false; // `invoice` campaigns never attach to a unit
}
/**
 * The promotions that can price this supplier code on `date`.
 *
 * Only `active` ones, of this supplier, valid on the date, whose scope covers the code, and never a
 * spend campaign. If any of them names the code explicitly (`items`), the line-wide ones are dropped.
 * That is what lets «Difarvet does not bonus Santgar» and «Difarvet gives 10+2 on INODIL 5 MG» coexist.
 */
function selectApplicablePromotions(promotions, target, date) {
    const day = date.slice(0, 10);
    const matching = promotions.filter((p) => p.status === supplier_linking_enums_1.PromotionStatus.active &&
        p.supplierQvetId === target.supplierQvetId &&
        p.mechanic.type !== supplier_linking_enums_1.PromotionMechanicType.spend_goal &&
        isPromotionValidOn(p, day) &&
        scopeMatches(p, target));
    const specific = matching.filter((p) => p.scope.kind === supplier_linking_enums_1.PromotionScopeKind.items);
    return specific.length ? specific : matching;
}
const round2 = (n) => Math.round(n * 100) / 100;
function withoutVat(price, input) {
    return input.vatIncluded && input.vatRate > 0 ? price / (1 + input.vatRate / 100) : price;
}
function priceOnBasis(promotion, input) {
    if (promotion.priceBasis === supplier_linking_enums_1.PromotionPriceBasis.public) {
        return input.pricePublic === undefined ? undefined : withoutVat(input.pricePublic, input);
    }
    return withoutVat(input.priceVet, input);
}
function outcomeOf(promotion, input) {
    const lineFactor = 1 - (input.lineDiscountPct ?? 0) / 100;
    const vet = withoutVat(input.priceVet, input) * lineFactor;
    const q = input.quantity;
    if (!promotion || promotion.mechanic.type === supplier_linking_enums_1.PromotionMechanicType.none) {
        return { paidQuantity: q, freeQuantity: 0, total: q * vet, promotion, label: "sin promoción" };
    }
    const basis = priceOnBasis(promotion, input);
    if (basis === undefined)
        return undefined; // public basis without a public price: cannot price it
    const unit = basis * lineFactor;
    const m = promotion.mechanic;
    switch (m.type) {
        case supplier_linking_enums_1.PromotionMechanicType.bonus: {
            // Buy `buy`, get `get` free, per complete block. A block not reached gives nothing.
            // `cheapest` only matters when several codes mix; for one code the free units cost the same.
            const blocks = Math.floor(q / m.buy);
            const free = blocks * m.get;
            return {
                paidQuantity: q,
                freeQuantity: free,
                total: q * unit,
                promotion,
                label: `${m.buy}+${m.get}${m.freeUnits === supplier_linking_enums_1.PromotionFreeUnits.cheapest ? " (las de menor valor)" : ""}`,
            };
        }
        case supplier_linking_enums_1.PromotionMechanicType.percent:
            return { paidQuantity: q, freeQuantity: 0, total: q * unit * (1 - m.percent / 100), promotion, label: `${m.percent} %` };
        case supplier_linking_enums_1.PromotionMechanicType.fixed_price: {
            const row = m.prices.find((p) => p.sku === input.sku);
            if (!row)
                return undefined;
            return { paidQuantity: q, freeQuantity: 0, total: q * withoutVat(row.price, input) * lineFactor, promotion, label: `precio fijo ${row.price}` };
        }
        default:
            return undefined;
    }
}
function perOurUnit(outcome, total, input) {
    const units = (outcome.paidQuantity + outcome.freeQuantity) * input.unitsPerSupplierUnit;
    return units > 0 ? total / units : 0;
}
function earlyPaymentFactor(input, promotion) {
    const ep = input.earlyPayment;
    if (!ep || !input.willPayOnTime)
        return 1;
    if (ep.requiresAgreement && !input.earlyPaymentAgreed)
        return 1;
    if (promotion && promotion.stacking && !promotion.stacking.withEarlyPayment)
        return 1;
    return 1 - ep.percent / 100;
}
/**
 * What one of our purchase units really costs from one supplier, at `quantity`.
 *
 * Evaluates «no promotion» and every candidate, and keeps the cheapest: two applicable promotions
 * never add up (they are «no acumulables»), the order simply takes the better one.
 */
function computeEffectiveCost(input) {
    if (input.quantity <= 0)
        throw new Error("quantity must be positive");
    if (input.unitsPerSupplierUnit <= 0)
        throw new Error("unitsPerSupplierUnit must be positive");
    const candidates = [undefined, ...input.promotions]
        .map((p) => outcomeOf(p, input))
        .filter((o) => !!o)
        .map((o) => {
        const factor = earlyPaymentFactor(input, o.promotion);
        const totalCost = o.total * factor;
        return { o, factor, totalCost, unitCost: perOurUnit(o, totalCost, input) };
    })
        .sort((a, b) => a.unitCost - b.unitCost);
    const best = candidates[0];
    const steps = [
        `precio de lista ${input.priceVet}${input.vatIncluded && input.vatRate > 0 ? ` con IVA ÷ ${1 + input.vatRate / 100}` : ""}`,
        `${input.quantity} × ${input.unitsPerSupplierUnit} de las nuestras`,
        `promoción: ${best.o.label}${best.o.freeQuantity ? ` → ${best.o.freeQuantity} de regalo` : ""}`,
        best.factor < 1 ? `pronto pago −${input.earlyPayment.percent} %` : "sin pronto pago",
    ];
    return {
        unitCost: round2(best.unitCost),
        paidQuantity: best.o.paidQuantity,
        freeQuantity: best.o.freeQuantity,
        totalBeforeEarlyPayment: round2(best.o.total),
        totalCost: round2(best.totalCost),
        appliedPromotionId: best.o.promotion?.id,
        earlyPaymentApplied: best.factor < 1,
        steps,
    };
}
/** Thresholds to change supplier on one line (compras-promociones §5). */
exports.HABITUAL_CHANGE_MIN_ANNUAL_SAVING = 150;
exports.HABITUAL_CHANGE_MIN_PERCENT = 2;
/**
 * Decision D3 (Javier, 2026-09-18): the habitual supplier follows the preference order among the
 * suppliers that sell the article, **unless** another one is cheaper with permanent conditions by at
 * least $150 a year **and** 2 %. Temporary promotions never move the habitual supplier.
 */
function recommendHabitual(candidates, annualUnits) {
    if (!candidates.length)
        return undefined;
    const byRankSorted = [...candidates].sort((a, b) => (a.preferenceRank ?? Number.MAX_SAFE_INTEGER) - (b.preferenceRank ?? Number.MAX_SAFE_INTEGER));
    const ranked = byRankSorted[0];
    const cheapest = [...candidates].sort((a, b) => a.permanentUnitCost - b.permanentUnitCost)[0];
    const diff = ranked.permanentUnitCost - cheapest.permanentUnitCost;
    const annualSaving = round2(diff * annualUnits);
    const percent = ranked.permanentUnitCost > 0 ? (diff / ranked.permanentUnitCost) * 100 : 0;
    const shouldChange = cheapest.supplierQvetId !== ranked.supplierQvetId &&
        annualSaving >= exports.HABITUAL_CHANGE_MIN_ANNUAL_SAVING &&
        percent >= exports.HABITUAL_CHANGE_MIN_PERCENT;
    return {
        byRank: ranked.supplierQvetId,
        byCost: cheapest.supplierQvetId,
        recommended: shouldChange ? cheapest.supplierQvetId : ranked.supplierQvetId,
        shouldChange,
        annualSaving: shouldChange ? annualSaving : 0,
    };
}
